import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Hammer, CheckCircle2, ShieldAlert } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

// Modules deferred to a later operational build (per the first-implementation
// boundary). Each shows its confirmed responsibilities so the workspace is
// complete and navigable without fabricating records.
const MODULES = {
  incidents: {
    title: 'Incidents',
    icon: '/app_incidents_n.png',
    accent: '#DC2626',
    intro: 'Operational and safety incidents with people, customer and location involved, photographs, supporting documentation, and required investigation and follow-up.',
    points: ['People, customer and location involved', 'Photographs and supporting documentation', 'Required investigation and follow-up', 'Feeds Corrective Actions when a deficiency results'],
    countUrl: '/incident-reports', countLabel: 'incident report(s) already on record',
  },
  inventory: {
    title: 'Inventory',
    icon: '/app_inventory_n.png',
    accent: '#D946EF',
    intro: 'Operational supplies, equipment and assigned inventory with stock levels, assignments and alerts.',
    points: ['Operational supplies & equipment', 'Stock levels and low-stock alerts', 'Assignments to personnel / locations'],
  },
  'preventive-maintenance': {
    title: 'Preventive Maintenance',
    icon: '/app_pmp_n.png',
    accent: '#94A3B8',
    intro: 'Scheduled facility and equipment maintenance with recurring tasks, due dates and completion history.',
    points: ['Scheduled facility & equipment maintenance', 'Recurring tasks and due dates', 'Completion history'],
  },
  training: {
    title: 'Training',
    icon: '/app_training_n.png',
    accent: '#FACC15',
    intro: 'Operational training and certifications drawing on TEAM personnel records — assigned courses, completion tracking and renewal reminders. TEAM remains the company-wide HR system of record; Training uses its personnel data without duplicating it.',
    points: ['Assigned operational training & certifications', 'Completion tracking and renewal reminders', 'Uses TEAM personnel records (no duplication)'],
  },
  vehicles: {
    title: 'Vehicles',
    icon: '/app_vehicles_n.png',
    accent: '#2563EB',
    intro: 'Vehicle records, vehicle inspections, maintenance schedules, assigned drivers, and registration & insurance expiration tracking.',
    points: ['Vehicle records & assigned drivers', 'Vehicle inspections & maintenance schedules', 'Registration and insurance expiration tracking'],
    countUrl: '/vehicles', countLabel: 'vehicle(s) already on record',
  },
};

const OperationsPlaceholder = ({ module }) => {
  const { token } = useContext(AuthContext);
  const cfg = MODULES[module] || MODULES.incidents;
  const [count, setCount] = useState(null);

  useEffect(() => {
    setCount(null);
    if (cfg.countUrl) {
      axios.get(`${API}${cfg.countUrl}`, auth(token)).then((r) => setCount(Array.isArray(r.data) ? r.data.length : null)).catch(() => {});
    }
  }, [module, token]);

  return (
    <div data-testid={`ops-placeholder-${module}`}>
      <div className="mb-6 flex items-center gap-4">
        <img src={cfg.icon} alt={cfg.title} className="h-14 w-14 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.18em] text-[#8FC93A]">Operations Module</div>
          <h1 className="text-[30px] leading-tight font-extrabold tracking-tight text-[#0E2A47]">{cfg.title}</h1>
        </div>
      </div>

      <div className="max-w-3xl rounded-2xl border border-slate-200 bg-white p-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-700">
          <Hammer size={13} /> Prepared for operational build
        </div>
        <p className="mt-4 text-base leading-relaxed text-slate-600">{cfg.intro}</p>

        <div className="mt-5 space-y-2">
          {cfg.points.map((p, i) => (
            <div key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#8FC93A]" /> {p}
            </div>
          ))}
        </div>

        {count !== null && (
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid={`ops-placeholder-count-${module}`}>
            <span className="font-bold text-[#0E2A47]">{count}</span> {cfg.countLabel} — governing data preserved; this module will surface it without duplication.
          </div>
        )}

        <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 text-xs text-slate-500" data-testid={`ops-placeholder-purge-note-${module}`}>
          <ShieldAlert size={15} className="mt-0.5 shrink-0 text-slate-400" />
          <span>No records are stored here yet, so there is no purge control. The Global-Administrator purge activates automatically once this module starts storing records — using the same shared purge pattern as Inspections, Corrective Actions, Incidents and Vehicles.</span>
        </div>
      </div>
    </div>
  );
};

export default OperationsPlaceholder;
